import type { CSSProperties } from 'react';

import { dropdownOptionTokens } from './dropdown-option.tokens.ts';
import type {
  DropdownOption,
  DropdownOptionCheckbox,
  DropdownOptionDivider,
  DropdownOptionEpc,
  DropdownOptionGroupHeader,
  DropdownOptionTextWithIcon,
} from './types';

export const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: `${dropdownOptionTokens.fieldGap}px`,
  minWidth: 0,
};

export const labelStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Dropdown rows use fieldGap; reset checkbox label margin from KestrixCheckbox. */
export const checkboxShellSx = {
  pointerEvents: 'none',
  flexShrink: 0,
  display: 'inline-flex',
  '& .MuiCheckbox-root': {
    marginRight: 0,
    padding: 0,
    width: 20,
    height: 20,
  },
};

export type DropdownOptionType =
  | 'text'
  | 'text-with-icon'
  | 'checkbox'
  | 'epc';

export function isCheckboxOption(
  option: DropdownOption
): option is DropdownOptionCheckbox {
  return 'type' in option && option.type === 'checkbox';
}

export function isEpcOption(
  option: DropdownOption
): option is DropdownOptionEpc {
  return 'type' in option && option.type === 'epc';
}

export function isTextWithIconOption(
  option: DropdownOption
): option is DropdownOptionTextWithIcon {
  return 'type' in option && option.type === 'text-with-icon';
}

export function isDividerOption(
  option: DropdownOption
): option is DropdownOptionDivider {
  return 'type' in option && option.type === 'divider';
}

export function isGroupHeaderOption(
  option: DropdownOption
): option is DropdownOptionGroupHeader {
  return 'type' in option && option.type === 'group-header';
}

export type DropdownSelectableOption = Exclude<
  DropdownOption,
  DropdownOptionDivider | DropdownOptionGroupHeader
>;

export function isSelectableOption(
  option: DropdownOption
): option is DropdownSelectableOption {
  return !isDividerOption(option) && !isGroupHeaderOption(option);
}

export type DropdownOptionFieldPresentationProps = {
  checked?: boolean;
  isDisabled?: boolean;
  contentColor?: string;
  showSelectionCheckbox?: boolean;
};

export function resolveFieldLabelColor(
  isDisabled: boolean,
  contentColor?: string,
): 'disabled' | 'body-accent' | undefined {
  return isDisabled ? 'disabled' : contentColor ? undefined : 'body-accent';
}
